import {Button, Input, toast} from '@cashiaApp/web-components';
import {AxiosHeaders} from 'axios';
import CryptoJS from 'crypto-js';
import React, {ChangeEvent, FormEvent, useCallback, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import Info from '../../assets/icons/info-circle.svg';
import Spinner from '../../components/tailwind/Spinner';
import {apiUrl} from '../../utils/constants';
import EyeSlash from './../../assets/icons/eye-slash.svg';
import Eye from './../../assets/icons/eye.svg';
import {BodyFormValues, checkout} from './api';

interface FormValues {
  amount: number;
  currency: string;
  requestId: string;
  successCallbackUrl: string;
  errorCallbackUrl: string;
  callbackUrl: string;
  secretKey: string;
  keyId: string;
}
interface FormErrors {
  [key: string]: string;
}

type ValidateFunction = (values: FormValues) => FormErrors;

const useFormValidation = (
  initialState: FormValues,
  validate: ValidateFunction
) => {
  const [values, setValues] = useState<FormValues>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const reset = useCallback(() => {
    setValues(initialState);
    setErrors({});
    setErrors({});
    setIsSubmitting(false);
  }, [initialState]);

  return {
    handleChange,
    reset,
    values,
    setValues,
    errors,
    setErrors,
    isSubmitting,
    validate,
  };
};

const initialState: FormValues = {
  amount: 1000,
  currency: 'KES',
  requestId: uuidv4(),
  successCallbackUrl: 'https://www.cashia.com/',
  errorCallbackUrl: 'https://www.cashia.com/',
  callbackUrl: 'https://www.cashia.com/',
  secretKey: 'test',
  keyId: 'test',
};

const validate: ValidateFunction = (values) => {
  const errors: FormErrors = {};

  if (!values.amount) {
    errors.firstName = 'Amount is required';
  }
  if (!values.currency.trim()) {
    errors.lastName = 'Currency is required';
  }
  if (!values.requestId.trim()) {
    errors.phoneNumber = 'Request is required';
  }
  if (!values.successCallbackUrl.trim()) {
    errors.successCallbackUrl = 'CallbackUrl is required';
  } else {
    const urlPattern =
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
    const invalidCharactersPattern = /[ !@#]/;
    if (!urlPattern.test(values.successCallbackUrl)) {
      errors.successCallbackUrl = 'Please enter a valid callbackUrl address';
    } else if (invalidCharactersPattern.test(values.successCallbackUrl)) {
      errors.successCallbackUrl =
        'Please enter a valid Website URL without spaces or special characters like !, @, #';
    }
  }
  if (!values.errorCallbackUrl.trim()) {
    errors.errorCallbackUrl = 'CallbackUrl is required';
  } else {
    const urlPattern =
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
    const invalidCharactersPattern = /[ !@#]/;
    if (!urlPattern.test(values.errorCallbackUrl)) {
      errors.errorCallbackUrl = 'Please enter a valid callbackUrl address';
    } else if (invalidCharactersPattern.test(values.errorCallbackUrl)) {
      errors.errorCallbackUrl =
        'Please enter a valid Website URL without spaces or special characters like !, @, #';
    }
  }
  return errors;
};

const CheckoutComponent: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [submissionType, setSubmissionType] = useState<
    'iframe' | 'redirect' | null
  >(null);

  const {handleChange, values, errors, setErrors} = useFormValidation(
    initialState,
    validate
  );

  const getHeaders = async (
    secret: string,
    keyId: string,
    requestBody: BodyFormValues
  ) => {
    const nonce = Math.random().toString(36).substring(2, 15);
    const timestamp = Math.floor(Date.now() / 1000);
    const method = 'POST';
    // const host = 'core-backend.stg.cashia.com';
    let host = apiUrl.replace('https://', '');
    host = host.replace('http://', '');
    host = host.replace('/api', '');
    // requestBody = JSON.parse(JSON.stringify(requestBody));
    const bodyHash = CryptoJS.HmacSHA256(
      JSON.stringify(requestBody),
      // '',
      secret
    ).toString(CryptoJS.enc.Hex);
    const signingString = `${host}${method}${timestamp}${nonce}${keyId}`;
    const signature = CryptoJS.HmacSHA256(signingString, secret).toString(
      CryptoJS.enc.Hex
    );
    const headers = new AxiosHeaders();
    headers.set('Content-Type', 'application/json');
    headers.set('X-Cashia-Key-ID', keyId);
    headers.set('X-Cashia-Timestamp', timestamp);
    headers.set('X-Cashia-Signature', signature);
    headers.set('X-Cashia-Hash', bodyHash);
    headers.set('X-Cashia-Nonce', nonce);
    return headers;
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setIsSubmitting(true);
    const validationErrors = validate(values);
    setErrors(validationErrors);
    const keyId = values.keyId;
    const secretKey = values.secretKey;
    const requestBody: BodyFormValues = {
      amount: Number(values.amount),
      currency: values.currency,
      requestId: values.requestId,
      successCallbackUrl: values.successCallbackUrl,
      errorCallbackUrl: values.errorCallbackUrl,
      callbackUrl: values.callbackUrl,
    };
    const headers = await getHeaders(secretKey, keyId, requestBody);

    try {
      const checkoutResponse = await checkout(requestBody, headers);
      if (submissionType === 'iframe') {
        setIframeSrc(checkoutResponse.url);
        setShowIframe(true);
      } else {
        window.location.href = checkoutResponse.url;
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const renderErrorMessage = (fieldName: string) => {
    if (errors[fieldName]) {
      return (
        <span className="flex items-center gap-2">
          <img src={Info} alt="Error" />
          <span className="text-orangeDark px-4 rounded text-sm font-normal bg-lightOrange">
            {errors[fieldName]}
          </span>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="h-screen w-screen flex justify-between">
      <div className="flex items-center flex-col w-full px-4 xl:px-0 lg:w-2/5 py-6 md:py-5 lg:py-4 2xl:py-20 overflow-y-auto">
        <div className="flex flex-col flex-grow max-w-md lg:ml-[60px] w-full">
          <form
            onSubmit={handleFormSubmit}
            method="POST"
            className="flex flex-col gap-2">
            {/* Form Fields */}
            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                Success CallbackUrl * {renderErrorMessage('successCallbackUrl')}
              </label>
              <Input
                name="successCallbackUrl"
                value={values.successCallbackUrl}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>
            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                Error CallbackUrl * {renderErrorMessage('errorCallbackUrl')}
              </label>
              <Input
                name="errorCallbackUrl"
                value={values.errorCallbackUrl}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>
            <div className="my-1">
              <label className="text-darkGrey flex gap-2 items-center text-sm font-bold mb-1">
                Amount * {renderErrorMessage('amount')}
              </label>
              <Input
                name="amount"
                type="number"
                value={values.amount}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>
            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                Currency * {renderErrorMessage('currency')}
              </label>
              <Input
                name="currency"
                value={values.currency}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>
            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                RequestId * {renderErrorMessage('requestId')}
              </label>
              <Input
                name="requestId"
                value={values.requestId}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>
            <div className="flex items-center mt-2 mb-1 gap-2">
              <p className="text-darkGrey text-sm font-bold font-['Metropolis']">
                KeyId
              </p>
              <p className="flex-1">{renderErrorMessage('keyId')}</p>
            </div>
            <div className="w-full flex justify-center items-center bg-transparent rounded-lg h-12 focus:ring-2 focus:ring-[#FECED8] relative">
              <Input
                name="keyId"
                value={values.keyId}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
              <div
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer">
                {showPassword ? (
                  <img src={Eye} height={16} width={16} />
                ) : (
                  <img src={EyeSlash} height={16} width={16} />
                )}
              </div>
            </div>
            <div className="flex items-center mt-2 mb-1 gap-2">
              <p className="text-darkGrey text-sm font-bold font-['Metropolis']">
                SecretKey
              </p>
              <p className="flex-1">{renderErrorMessage('secretKey')}</p>
            </div>
            <div className="w-full flex justify-center items-center bg-transparent rounded-lg h-12 focus:ring-2 focus:ring-[#FECED8] relative">
              <Input
                name="secretKey"
                value={values.secretKey}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
              <div
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer">
                {showPassword ? (
                  <img src={Eye} height={16} width={16} />
                ) : (
                  <img src={EyeSlash} height={16} width={16} />
                )}
              </div>
            </div>

            {/* Iframe and Redirect Buttons */}
            <div className="flex justify-between mt-4 gap-2">
              <Button
                type="submit"
                className="bg-smoothRed text-white w-1/2 h-12 rounded-lg hover:bg-hotPink"
                onClick={() => setSubmissionType('iframe')}
                disabled={isSubmitting}>
                {isLoading ? <Spinner className="fill-white" /> : 'Iframe'}
              </Button>
              <Button
                type="submit"
                className="bg-smoothRed text-white w-1/2 h-12 rounded-lg hover:bg-hotPink"
                onClick={() => setSubmissionType('redirect')}
                disabled={isSubmitting}>
                {isLoading ? <Spinner className="fill-white" /> : 'Redirect'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Iframe Content */}
      {showIframe && (
        <div className="w-2/5 p-6 bg-white shadow-lg rounded-lg">
          <iframe
            src={iframeSrc} // set this dynamically based on your logic
            width="100%"
            height="100%"
          />
        </div>
      )}
    </div>
  );
};

export default CheckoutComponent;
